// Google Analytics 4 service for Facto
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

class AnalyticsService {
  private isEnabled: boolean;
  private measurementId: string | undefined;

  constructor() {
    this.measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
    this.isEnabled = !!this.measurementId && this.measurementId.startsWith('G-');
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics]', this.isEnabled ? 'Enabled' : 'Disabled', this.measurementId);
    }
  }

  // Check if gtag is available
  private isReady(): boolean {
    return this.isEnabled && typeof window !== 'undefined' && typeof window.gtag === 'function';
  }

  // Track page views
  trackPageView(path: string, title?: string) {
    if (!this.isReady()) return;
    
    window.gtag('config', this.measurementId!, {
      page_path: path,
      page_title: title || document.title
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Page View:', path, title);
    }
  }

  // Track custom events
  trackEvent(eventName: string, parameters?: Record<string, any>) {
    if (!this.isReady()) return;
    
    window.gtag('event', eventName, parameters);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] Event:', eventName, parameters);
    }
  }

  // === USER ENGAGEMENT TRACKING ===
  
  trackUserRegistration(userType: 'buyer' | 'seller', method: string = 'email') {
    this.trackEvent('sign_up', {
      method,
      user_type: userType,
      event_category: 'engagement'
    });
  }

  trackUserLogin(userType: 'admin' | 'buyer' | 'seller', method: string = 'email') {
    this.trackEvent('login', {
      method,
      user_type: userType,
      event_category: 'engagement'
    });
  }

  trackEmailVerification(success: boolean) {
    this.trackEvent('email_verification', {
      success,
      event_category: 'engagement'
    });
  }

  // === BUSINESS METRICS TRACKING ===
  
  trackInvoiceUpload(invoiceId: string, amount: number, currency: string = 'PYG') {
    this.trackEvent('invoice_upload', {
      event_category: 'business',
      invoice_id: invoiceId,
      value: amount,
      currency,
      event_label: 'seller_action'
    });
  }

  trackInvoiceVerification(invoiceId: string, status: 'verified' | 'rejected') {
    this.trackEvent('invoice_verification', {
      event_category: 'admin',
      invoice_id: invoiceId,
      verification_status: status,
      event_label: 'admin_action'
    });
  }

  trackBidPlaced(invoiceId: string, bidAmount: number) {
    this.trackEvent('bid_placed', {
      event_category: 'business',
      invoice_id: invoiceId,
      value: bidAmount,
      currency: 'PYG',
      event_label: 'buyer_action'
    });
  }

  trackBidAccepted(invoiceId: string, bidAmount: number, transactionId?: string) {
    this.trackEvent('bid_accepted', {
      event_category: 'business',
      invoice_id: invoiceId,
      value: bidAmount,
      currency: 'PYG',
      transaction_id: transactionId,
      event_label: 'seller_action'
    });
  }

  trackBidRejected(invoiceId: string, bidAmount: number) {
    this.trackEvent('bid_rejected', {
      event_category: 'business',
      invoice_id: invoiceId,
      value: bidAmount,
      currency: 'PYG',
      event_label: 'seller_action'
    });
  }

  // E-commerce tracking for completed transactions
  trackTransaction(transactionId: string, value: number) {
    this.trackEvent('purchase', {
      transaction_id: transactionId,
      value,
      currency: 'PYG',
      event_category: 'ecommerce'
    });
  }

  // === USER BEHAVIOR TRACKING ===
  
  trackMarketplaceView(filterType?: string, sortBy?: string) {
    this.trackEvent('marketplace_view', {
      event_category: 'engagement',
      filter_type: filterType,
      sort_by: sortBy
    });
  }

  trackInvoiceDetailView(invoiceId: string, userType: string) {
    this.trackEvent('invoice_detail_view', {
      event_category: 'engagement',
      invoice_id: invoiceId,
      user_type: userType
    });
  }

  trackSearch(searchTerm: string, resultCount: number) {
    this.trackEvent('search', {
      search_term: searchTerm,
      result_count: resultCount,
      event_category: 'engagement'
    });
  }

  // === FORM TRACKING ===
  
  trackFormStart(formName: string) {
    this.trackEvent('form_start', {
      event_category: 'engagement',
      form_name: formName
    });
  }

  trackFormSubmit(formName: string, success: boolean = true) {
    this.trackEvent('form_submit', {
      event_category: 'engagement',
      form_name: formName,
      success
    });
  }

  trackFormError(formName: string, errorField: string) {
    this.trackEvent('form_error', {
      event_category: 'error',
      form_name: formName,
      error_field: errorField
    });
  }

  // === FILE UPLOAD TRACKING ===
  
  trackFileUpload(fileType: string, fileSize: number, uploadType: 'invoice_front' | 'invoice_back') {
    this.trackEvent('file_upload', {
      event_category: 'engagement',
      file_type: fileType,
      file_size: fileSize,
      upload_type: uploadType
    });
  }

  // === ERROR TRACKING ===
  
  trackError(errorMessage: string, errorType: string = 'javascript', context?: string) {
    this.trackEvent('exception', {
      description: errorMessage,
      fatal: false,
      error_type: errorType,
      context,
      event_category: 'error'
    });
  }

  trackApiError(endpoint: string, statusCode: number, errorMessage: string) {
    this.trackEvent('api_error', {
      event_category: 'error',
      endpoint,
      status_code: statusCode,
      error_message: errorMessage
    });
  }

  // === USER IDENTIFICATION ===
  
  identifyUser(userId: string, userType: 'admin' | 'buyer' | 'seller', emailVerified: boolean = false) {
    if (!this.isReady()) return;
    
    window.gtag('set', 'user_properties', {
      user_id: userId,
      user_type: userType,
      email_verified: emailVerified
    });

    if (process.env.NODE_ENV === 'development') {
      console.log('[Analytics] User Identified:', userId, userType);
    }
  }

  // === CONVERSION FUNNEL TRACKING ===
  
  trackFunnelStep(step: 'registration' | 'verification' | 'first_upload' | 'first_bid' | 'first_transaction', userType: string) {
    this.trackEvent('funnel_step', {
      event_category: 'conversion',
      funnel_step: step,
      user_type: userType
    });
  }
}

export default new AnalyticsService();